// src/components/Study/Exam/ExplanationDisplay.tsx
import React, { useState } from 'react';
import parse from 'html-react-parser';
import { VignetteContent } from '../../../../types/Vignette';
import ImageDisplay from '../../../Misc/ImageDisplay';
import { ArrowDown, ArrowUp } from 'react-feather';
import { RootState } from '../../../../store/store';
import { useSelector } from 'react-redux';
import { updateCurrentUserInfo } from '../../../../services/auth';
import { hapticsImpactLight } from '../../../../utils/haptics';
import Tooltip from '@mui/material/Tooltip';

interface ExplanationDisplayProps {
  parsedExhibit: React.ReactNode | null;
  parsedExplanation: React.ReactNode | null;
  activeVignetteContent: VignetteContent | undefined;
}

const ExplanationDisplay: React.FC<ExplanationDisplayProps> = ({
  parsedExhibit,
  parsedExplanation,
  activeVignetteContent,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const [learningObjectiveLocation, setLearningObjectiveLocation] = useState(
    currentUser?.preferredLearningObjectiveLocation || 'bottom'
  );

  if (!activeVignetteContent) {
    return null;
  }

  const {
    Image,
    learningObjective,
    references,
    id: contentId,
  } = activeVignetteContent;

  const toggleLearningObjectiveLocation = async () => {
    hapticsImpactLight();
    const updatedUser = await updateCurrentUserInfo({
      preferredLearningObjectiveLocation:
        learningObjectiveLocation === 'top' ? 'bottom' : 'top',
    });
    if (updatedUser) {
      setLearningObjectiveLocation(
        updatedUser.preferredLearningObjectiveLocation
      );
    }
  };

  const renderLearningObjective = (position: 'top' | 'bottom') => {
    return (
      <div className="block block--lo">
        <Tooltip
          title={`Move Learning Objective to ${position === 'top' ? 'bottom' : 'top'}.`}
          enterDelay={250}
          enterTouchDelay={0}
        >
          <button
            className="button button--icon-only button--toggle-location"
            onClick={toggleLearningObjectiveLocation}
          >
            {position === 'top' ? <ArrowDown /> : <ArrowUp />}
          </button>
        </Tooltip>
        <strong>Learning Objective: </strong>
        {parse(learningObjective)}
      </div>
    );
  };

  return (
    <div className="explanation">
      {/* Learning Objective */}
      {learningObjectiveLocation === 'top' &&
        learningObjective &&
        renderLearningObjective('top')}

      {/* Exhibit within the explanation panel */}
      {parsedExhibit && <div className="exhibit">{parsedExhibit}</div>}

      {/* Images */}
      {Image && Image.length > 0 && (
        <ImageDisplay
          images={Image}
          displayMainImage={true}
          activeContentId={contentId || ''} // Pass content ID for context if needed by ImageDisplay
        />
      )}

      {/* Main Explanation Body */}
      {parsedExplanation}

      {/* Learning Objective */}
      {learningObjectiveLocation === 'bottom' &&
        learningObjective &&
        renderLearningObjective('bottom')}

      {/* References */}
      {references && references.length > 0 && (
        <div className="references">
          <ul className={references.length === 1 ? 'one-ref' : ''}>
            {references.map((reference, index) => (
              <li key={`${contentId}-ref-${index}`}>{parse(reference)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ExplanationDisplay;
